import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { courseApi, authFetch, apiUrl, authService } from '../utils/api';
import { useUser } from '../contexts/UserContext';

interface FormData {
  courseName: string;
  grade: string;
  term: string;
  active: boolean;
  price: string;
  description: string;
  imageFile: File | null;
  teacherIds: string[]; // Changed to support multiple teachers
}

interface Teacher {
  id: string;
  fullName: string;
  email: string;
  active: boolean;
}

const AddCourse = () => {
  const navigate = useNavigate();
  const { user, isTeacher, isModerator } = useUser();
  const [formData, setFormData] = useState<FormData>({
    courseName: '',
    grade: '',
    term: '',
    active: false,
    price: '',
    description: '',
    imageFile: null,
    teacherIds: [], // Changed to support multiple teachers
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);

  // Fetch teachers for Moderator role
  useEffect(() => {
    if (isModerator()) {
      fetchTeachers();
    } else if (isTeacher() && user) {
      // Auto-select current teacher as primary
      setFormData(prev => ({ ...prev, teacherIds: [user.id] }));
    }
  }, [user, isModerator, isTeacher]);

  const fetchTeachers = async () => {
    setLoadingTeachers(true);
    try {
      const response = await authFetch(apiUrl('Teacher'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setTeachers(data.data.filter((teacher: Teacher) => teacher.active));
      } else {
        throw new Error(data.message || 'Failed to fetch teachers');
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setError('فشل في تحميل قائمة المعلمين');
      setTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Validate at least one teacher is selected
      if (formData.teacherIds.length === 0) {
        setError('يرجى اختيار المعلم الأول على الأقل');
        setIsSubmitting(false);
        return;
      }

      // Validate maximum 2 teachers
      if (formData.teacherIds.length > 2) {
        setError('يمكن تعيين معلمين فقط لكل دورة');
        setIsSubmitting(false);
        return;
      }

      // Create FormData for multipart/form-data submission
      const formDataToSend = new FormData();
      formDataToSend.append('courseName', formData.courseName);
      formDataToSend.append('grade', formData.grade);
      formDataToSend.append('term', formData.term);
      formDataToSend.append('active', formData.active.toString());
      formDataToSend.append('price', formData.price);
      
      // Append TeacherIds array - backend expects array format
      formData.teacherIds.forEach((teacherId, index) => {
        formDataToSend.append(`TeacherIds[${index}]`, teacherId);
      });
      
      // Also append TeacherId for backward compatibility (first teacher as primary)
      if (formData.teacherIds.length > 0) {
        formDataToSend.append('teacherId', formData.teacherIds[0]);
      }
      
      if (formData.description) {
        formDataToSend.append('description', formData.description);
      }
      
      if (formData.imageFile) {
        formDataToSend.append('imageFile', formData.imageFile);
      }

      const response = await courseApi.addCourseWithImage(formDataToSend);
      
      if (response.success) {
        navigate('/courses');
      } else {
        setError(response.message || 'Failed to create course');
      }
    } catch (error) {
      console.error('Error creating course:', error);
      setError('An error occurred while creating the course');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({
      ...prev,
      imageFile: file,
    }));
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-dark">Add New Course</h1>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {error && (
          <div className="mb-4 p-4 text-red-700 bg-red-100 border border-red-300 rounded-md">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Teacher Selection - Only for Moderators */}
          {isModerator() && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  المدرس الأول (Primary) *
                </label>
                <select
                  value={formData.teacherIds[0] || ''}
                  onChange={(e) => {
                    const newTeacherIds = [...formData.teacherIds];
                    if (e.target.value) {
                      newTeacherIds[0] = e.target.value;
                      // Remove if already selected as second teacher
                      if (newTeacherIds[1] === e.target.value) {
                        newTeacherIds[1] = '';
                      }
                    } else {
                      newTeacherIds[0] = '';
                    }
                    setFormData(prev => ({ ...prev, teacherIds: newTeacherIds.filter(id => id !== '') }));
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
                  required
                  disabled={loadingTeachers}
                >
                  <option value="">اختر المدرس الأول</option>
                  {teachers.map((teacher) => (
                    <option 
                      key={teacher.id} 
                      value={teacher.id}
                      disabled={formData.teacherIds[1] === teacher.id}
                    >
                      {teacher.fullName}
                    </option>
                  ))}
                </select>
                {loadingTeachers && (
                  <p className="mt-1 text-sm text-gray-500">جاري تحميل المعلمين...</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  المدرس الثاني (Optional)
                </label>
                <select
                  value={formData.teacherIds[1] || ''}
                  onChange={(e) => {
                    const newTeacherIds = [...formData.teacherIds];
                    if (e.target.value) {
                      // Remove if already selected as first teacher
                      if (newTeacherIds[0] === e.target.value) {
                        return;
                      }
                      newTeacherIds[1] = e.target.value;
                    } else {
                      newTeacherIds[1] = '';
                    }
                    setFormData(prev => ({ ...prev, teacherIds: newTeacherIds.filter(id => id !== '') }));
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
                  disabled={loadingTeachers || !formData.teacherIds[0]}
                >
                  <option value="">اختر المدرس الثاني (اختياري)</option>
                  {teachers
                    .filter(teacher => teacher.id !== formData.teacherIds[0])
                    .map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.fullName}
                      </option>
                    ))}
                </select>
                {formData.teacherIds.length === 2 && (
                  <p className="mt-1 text-sm text-green-600">تم تعيين معلمين للدورة</p>
                )}
              </div>
            </>
          )}

          {/* Teacher Info Display - Only for Teachers */}
          {isTeacher() && user && (
            <div>
              <label className="block text-sm font-medium text-gray-700">المدرس</label>
              <div className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 bg-gray-50 text-gray-700">
                {user.name}
              </div>
              <p className="mt-1 text-sm text-gray-500">سيتم تعيينك كمعلم رئيسي للدورة</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Course Name *</label>
            <input
              type="text"
              name="courseName"
              value={formData.courseName}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Grade *</label>
            <select
              name="grade"
              value={formData.grade}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
              required
            >
              <option value="">Select Grade</option>
              <option value="0">Secondary 1</option>
              <option value="1">Secondary 2</option>
              <option value="2">Secondary 3</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Term *</label>
            <select
              name="term"
              value={formData.term}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
              required
            >
              <option value="">Select Term</option>
              <option value="0">First Term</option>
              <option value="1">Second Term</option>
              <option value="2">Third Term</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Price *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm border px-3 py-2"
              placeholder="Optional course description"
            />
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Course Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
            />
            {formData.imageFile && (
              <p className="mt-1 text-sm text-gray-600">Selected: {formData.imageFile.name}</p>
            )}
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/courses')}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Add Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCourse; 